import { StringIndexedObject, useToast } from '@kanda-libs/ks-design-library';
import {
  actions,
  AuthUser,
  revalidateUser,
  useSignIn,
} from '@kanda-libs/ks-frontend-services';
import { AsyncThunkActionError } from '@kanda-libs/ks-frontend-services/src/store/types';
import { URLS } from 'config';
import { useApiError, useAppDispatch, useLogout } from 'hooks';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { selectors } from 'store';

export interface GoogleSignInHook {
  signInWithGoogle: () => void;
  isSubmitting: boolean;
  disabled: boolean;
}

/**
 * Handles error extraction from the error object
 */
export const getGoogleSignInError = (
  error: StringIndexedObject<string>,
): string => {
  const { code } = error;
  if (code === 'auth/user-not-found')
    return 'User is not signed up with this email';
  if (code === 'auth/wrong-password')
    return 'Invalid user credentials provided';
  if (code === 'auth/popup-blocked')
    return 'Login popup blocked - allow popups or click the button again';
  if (code === 'auth/popup-closed-by-user')
    return 'Login popup close - please click the button again';
  return 'An error has occurred';
};

export function useGoogleSignIn(successUrl = URLS.home): GoogleSignInHook {
  const dispatch = useAppDispatch();
  const handleApiError = useApiError(
    'Unknown error signing in - please refresh and try again',
  );
  const { mutate: googleSignIn, isLoading: signInIsSubmitting } =
    useSignIn('googlePopup');
  const handleLogout = useLogout(false);
  const { showError } = useToast();
  const { push } = useHistory();
  const meIsSubmitting = useSelector(selectors.authUser.getIsSubmitting);

  const refreshUser = useCallback(async () => {
    dispatch(
      actions.me({
        onError: (error: AsyncThunkActionError) => {
          handleLogout(false);
          handleApiError(error);
        },
        onSuccess: (me: AuthUser) => {
          const { role } = me;
          if (!role) {
            showError(
              'No role associated to user - please try logging in again or contact Ideal4Finance',
            );
            handleLogout(false);
            return;
          }
          if (role !== 'staff') {
            showError(
              'User is not Ideal4Finance staff - please try logging in again or contact Ideal4Finance',
            );
            handleLogout(false);
            return;
          }
          dispatch(revalidateUser()).then(() => push(successUrl));
        },
      }),
    );
  }, [dispatch, handleLogout, push, handleApiError, showError, successUrl]);

  const signInWithGoogle = useCallback(() => {
    googleSignIn(false).then(({ error: signInError }) => {
      if (signInError) {
        showError(getGoogleSignInError(signInError));
        handleLogout(false);
        return;
      }
      refreshUser();
    });
  }, [googleSignIn, handleLogout, showError, refreshUser]);

  const isSubmitting = useMemo(
    () => Boolean(signInIsSubmitting || meIsSubmitting),
    [signInIsSubmitting, meIsSubmitting],
  );

  const disabled = useMemo(() => Boolean(isSubmitting), [isSubmitting]);

  return {
    signInWithGoogle,
    isSubmitting,
    disabled,
  };
}
